import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useMemo } from 'react';
import { ServerTable } from 'shared/ui/ServerTable';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { useGetCurrencySymbol } from 'app/appState';
import { ExportEntity, ExportModal, getIncomeStatisticsColumnKeys, useOpenExportModal } from 'features/ExportModal';
import { ReactComponent as ExportIcon } from 'shared/assets/icons/ExportIcon.svg';
import { BoxOccupancyObject, useGetIncomeStatisticsQuery } from 'entities/Warehouse';
import { useTableFilterContext } from 'features/TableFilter';
import { ServerTableActions } from 'shared/ui/ServerTable/types';
import { IncomeStatisticsObject } from 'entities/Warehouse/model/types';
import { transformIncomeStatisticsFilters } from '../utils/transformIncomeStatisticsFilters';
import { IncomeStatisticsFilter } from './IncomeStatisticsFilter';

export const IncomeStatisticsTable: FC = memo(() => {
  const currencySymbol = useGetCurrencySymbol();

  const { openExportModal, isExportEnabled } = useOpenExportModal();

  const { t } = useAppTranslation(['contracts', 'common']);

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const columns = useMemo<ColumnsType<IncomeStatisticsObject>>(
    () => [
      {
        title: t('Date'),
        width: '9%',
        key: 'date',
        render: (_, record) => <div className="font-semibold">{record.date}</div>,
      },
      {
        title: t('Rent amount'),
        width: '9%',
        key: 'rentAmount',
        render: (_, record) => (
          <div className="font-semibold">
            {record.rentAmount} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Rent credited'),
        width: '9%',
        key: 'rentCredited',
        render: (_, record) => (
          <div className="font-semibold">
            {record.rentCredited} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Insurance amount'),
        width: '9%',
        key: 'insuranceAmount',
        render: (_, record) => (
          <div className="font-semibold">
            {record.insuranceAmount} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Insurance credited'),
        width: '9%',
        key: 'insuranceCredited',
        render: (_, record) => (
          <div className="font-semibold">
            {record.insuranceCredited} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Deposit amount'),
        width: '9%',
        key: 'depositAmount',
        render: (_, record) => (
          <div className="font-semibold">
            {record.depositAmount} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Deposit credited'),
        width: '9%',
        key: 'depositCredited',
        render: (_, record) => (
          <div className="font-semibold">
            {record.depositCredited} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Other'),
        width: '9%',
        key: 'otherAmount',
        render: (_, record) => (
          <div className="font-semibold">
            {record.otherAmount} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Other credited'),
        width: '9%',
        key: 'otherCredited',
        render: (_, record) => (
          <div className="font-semibold">
            {record.otherCredited} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('write-off of a liability'),
        width: '9%',
        key: 'selfIssuedReceiptAmount',
        render: (_, record) => (
          <div className="font-semibold">
            {record.selfIssuedReceiptAmount} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('VAT'),
        width: '9%',
        key: 'vatAmount',
        render: (_, record) => (
          <div className="font-semibold">
            {record.vatAmount} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('VAT credited'),
        width: '9%',
        key: 'vatCredited',
        render: (_, record) => (
          <div className="font-semibold">
            {record.vatCredited} {currencySymbol}
          </div>
        ),
      },
    ],
    [t, currencySymbol],
  );

  const tableActions: Array<ServerTableActions<BoxOccupancyObject>> = useMemo(() => {
    const exportAction: Array<ServerTableActions<BoxOccupancyObject>> = isExportEnabled
      ? [
          {
            name: t('Export', { ns: 'common' }),
            theme: 'secondary',
            icon: <ExportIcon />,
            iconPosition: 'prev',
            onClick: () => {
              openExportModal({
                filters: transformIncomeStatisticsFilters(appliedFilters),
                columns: getIncomeStatisticsColumnKeys(t),
                entity: ExportEntity.INCOME_STATISTICS,
              });
            },
          },
        ]
      : [];

    return [
      ...exportAction,
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ];
  }, [appliedFilters, isExportEnabled, openExportModal, setFiltersOpened, t]);

  return (
    <div className="pt-4">
      <div className="font-semibold text-3xl mb-7">{t('Statistics')}</div>
      <ServerTable
        columns={columns}
        fetchData={useGetIncomeStatisticsQuery}
        rowKey="date"
        tableActionsPosition="prev"
        tableActions={tableActions}
        tags={tags}
        onAllFiltersClear={clearAllFilters}
        defaultFilters={transformIncomeStatisticsFilters(appliedFilters)}
      />
      <ExportModal />
      <IncomeStatisticsFilter />
    </div>
  );
});
